// The PDF report request (internal/export/screw_connector/data.go): what the engine computed, as data in no
// language - the server writes every text of the report, in the user's language. Only the 3D views and the screw's
// photo, the page's own pictures, travel as images.
import { cltRhokWideFace, type CltLayup } from "../../../http/screwConnector/formulas/clt";
import { isPanelMat } from "../../../http/screwConnector/formulas/ctx";
import { TIMBER_TIPO_MAP_REVERSE } from "../../../http/screwConnector/formulas/data";
import { isUserTipo, panelKindOf } from "../../../http/screwConnector/formulas/materials";
import { kefLookup, type JohansenResult } from "../../../http/screwConnector/formulas/resistance";
import {
  isHbsTableApplicable,
  isLvlAxialEdgeFamily,
  isLvlWideFamily,
} from "../../../http/screwConnector/formulas/spacing";
import type { CalcState } from "../../../http/screwConnector/formulas/state";
import type {
  ReportCalculation,
  ReportClt,
  ReportElement,
  ReportGridScrew,
  ReportResistance,
  ReportSpacingRule,
  ScrewConnectorReportData,
} from "../../../http/screwConnector/types/report";
import { reportDate, type ReportContext } from "./reportContext";
import type { ReportImages } from "./view3dCapture";

export type ReportVersion = "full" | "compact";

// The 3D viewer's captures the report shows (data URLs).
export type ReportViews = Partial<Pick<ReportImages, "hero" | "iso" | "front" | "side">>;

// The catalog entries the report names, as the form holds them.
export type ReportReferencesInput = {
  element1TimberMaterialId?: string | null;
  element2TimberMaterialId?: string | null;
  loadDurationClassCode?: string | null;
};

export type ReportPayloadOptions = {
  version: ReportVersion;
  language: string;
  blockReason?: string | null;
  references: ReportReferencesInput;
  views: ReportViews;
  // the screw family's photo, base64 PNG
  screwImage?: string | null;
};

const base64Of = (dataUrl: string | null | undefined): string => (dataUrl ? dataUrl.replace(/^data:[^,]*,/, "") : "");

const localDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const localDateTime = (d: Date): string =>
  `${localDate(d)}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

// The rule element n's minimum distances come from, as the engine's computeSpacingAt picks it.
function spacingRule(n: 1 | 2, state: CalcState, ctx: ReportContext): ReportSpacingRule {
  const mat = ctx.materials[n];
  if (n === 1 && isPanelMat(ctx.connectionType))
    return panelKindOf(mat.specie, mat.classe) === "plywood" ? "panelPlywood" : "panel";
  // CLT: distances always from ETA-11/0030 Annex B, whatever the chosen standard
  if (n === 1 ? state.clt1 : state.clt2) return "clt";
  if (state.predrilled) return "predrilled";
  const family = state.screwRec?.family || ctx.family;
  if (/KKT/i.test(family)) return "kkt";
  if (TIMBER_TIPO_MAP_REVERSE[mat.tipo] === "LVL Softwood") {
    if (isLvlAxialEdgeFamily(family) && (state.d === 7 || state.d === 9)) return "lvlEdge";
    if (isLvlWideFamily(family)) return "lvlWide";
  }
  return isHbsTableApplicable(family, state.d) ? "hbs" : "ec5";
}

function element(n: 1 | 2, state: CalcState | null, ctx: ReportContext): ReportElement {
  const mat = ctx.materials[n];
  const user = isUserTipo(mat.tipo);
  return {
    userMaterial: user ? mat.tipo : "",
    customName: user ? (mat.customName ?? "") : "",
    spacing: ctx.spacing[n],
    spacingRule: state ? spacingRule(n, state, ctx) : "ec5",
  };
}

function clt(layup: CltLayup | null): ReportClt | null {
  if (!layup) return null;
  return {
    side: layup.latoInserimento,
    layers: layup.numStrati,
    totalThickness: layup.spessoreTotale,
    class: layup.classe,
    rhokWide: cltRhokWideFace(layup),
  };
}

function resistance(r: JohansenResult): ReportResistance {
  return {
    fvRk: r.FvRk,
    mode: r.mode ?? "",
    modes: r.modes,
    ropes: r.ropes ?? {},
    vars: {
      d: r.vars.d,
      t1: r.vars.t1 ?? null,
      t2: r.vars.t2 ?? null,
      fh1k: r.vars.fh1k ?? null,
      fh2k: r.vars.fh2k ?? null,
      tPlate: r.vars.tPlate ?? null,
      tWood: r.vars.tWood ?? null,
      fhk: r.vars.fhk ?? null,
      myRk: r.vars.myRk,
      beta: r.vars.beta ?? null,
      fax4: r.vars.Fax4,
    },
    steelTimber: r.vars.tPlate !== undefined,
    branch: r.branch ?? "",
    frac: r.frac ?? null,
    thin: r.thin ?? null,
    thick: r.thick ?? null,
    thinMode: r.thinMode ?? "",
    thickMode: r.thickMode ?? "",
    roles3: !!r.roles3,
    double: !!r.double,
    panelFlag: !!r.panelFlag,
    label: r.label ?? "",
    faxPlane: r.FaxPlane ?? null,
    planes: (r.planes ?? []).map(resistance),
    govLabel: r.govLabel ?? "",
  };
}

const gridScrew = (p: CalcState["grid"][number]): ReportGridScrew => ({ u: p.u, v: p.v, x: p.x, y: p.y, ok: p.ok });

function calculation(state: CalcState): ReportCalculation {
  const screw = state.screwRec;
  // k_ef of the row governing the shear n_ef (EC5 Table 8.1), as formulaNefShear looks it up
  const governing = state.nefShearGovernElem;
  const kef = kefLookup(
    governing === 1 ? state.s11 : state.s12,
    state.d,
    state.predrilled,
    governing === 2 && state.materiale1 === "acciaio",
  );
  return {
    steel: state.materiale1 === "acciaio",
    panel1: state.panel1,
    three: state.three,
    threeD: state.threeD,
    steel3: state.steel3,
    twoSides: state.twoSides,
    screwDiameter: screw?.geometria_mm.d1 ?? null,
    screwLength: screw?.geometria_mm.L ?? null,
    headDiameter: screw?.geometria_mm.dk ?? null,
    d: state.d,
    theta: state.theta,
    b1: state.b1,
    d1: state.d1,
    t1: state.t1,
    b2: state.b2,
    d2: state.d2,
    t2: state.t2,
    clt1: clt(state.clt1),
    clt2: clt(state.clt2),
    panelKind: state.panelKind1 ?? "",
    fn: state.fnKN,
    fv: state.fvKN,
    fa: state.faKN,
    forceF: state.forceF_kN,
    kmod: state.kmod,
    kmodPanel: state.kmodPanel,
    kmodWood: state.kmodWood,
    gammaM: state.gammaM,
    predrilled: state.predrilled,
    predrillReasons: state.predrillReasons,
    fh1k: state.fh1k,
    fh2k: state.fh2k,
    rhok1: state.rhok1,
    rhok2: state.rhok2,
    alpha1: state.alphaElemento1,
    alpha2: state.alphaElemento2,
    panelFhEquation: state.panelFh1?.eq ?? "",
    resistance: resistance(state.resistenza),
    faxRk: state.faxRk,
    faxCRk: state.faxCRk,
    faxRkEf: state.faxRkEf,
    faxRd: state.faxRd,
    threadDouble: state.threadD
      ? { f1: state.threadD.F1, f2: state.threadD.F2, f3: state.threadD.F3, fHead: state.threadD.Fhead }
      : null,
    threadFull: state.threadFull
      ? {
          f1: state.threadFull.F1,
          fHead: state.threadFull.Fhead,
          f2: state.threadFull.F2,
          fTens: state.threadFull.ftens,
          lefHead: state.threadFull.lefHead,
        }
      : null,
    nef: state.nef,
    nefShear: state.nefShear,
    nefShearElement: governing,
    kef,
    angleGrain1: state.angleGrainElem1,
    angleGrain2: state.angleGrainElem2,
    staggeredRows: state.sfalsaViti,
    gridRows: state.rows,
    gridCols: state.cols,
    s1: state.s11,
    s2: state.s12,
    s1Min: state.s11Min,
    s2Min: state.s12Min,
    spreadActive: state.spreadActive,
    totalScrews: state.viteTotali,
    admissibleScrews: state.viteAmmissibili,
    planes: state.nPiani,
    fvRkConnection: state.FvRkConnessione,
    fvRdConnection: state.FvRdUnione,
    fvRdScrew: state.FvRdVite,
    faxRkConnection: state.FaxRkUnione,
    faxRdConnection: state.FaxRdUnione,
    eta: state.eta,
    etaAxial: state.etaAxSimple,
    etaCombined: state.etaCombinata,
    etaSplitting: state.etaSplitting,
    he: state.heElem2,
    splitT: state.splitT,
    splitB: state.splitB,
    f90Rk: state.F90Rk,
    f90Rd: state.F90Rd,
    fvEdSplitting: state.FvEdSplitting,
    kser: state.kser,
    kserBranch: state.kserBranch ?? "",
    kserRhoM: state.kserRhoM,
    rhoMean1: state.rhomean1,
    rhoMean2: state.rhomean2,
    tipOverlap: state.tipOverlap,
    stagger: state.stagger,
    staggerDir: state.staggerDir ?? "",
    screwLengthEffective: state.screwLEff,
    tHead: state.tHead,
    tPoint: state.tPoint,
    washer: state.rondellaInfo
      ? { code: state.rondellaSelCodice ?? "", d2: state.rondellaInfo.d2, h: state.rondellaInfo.h }
      : null,
    grid: (state.grid ?? []).map(gridScrew),
    grid3: (state.gridB ?? []).map(gridScrew),
    continuous1: !!state.lines1?.continuo1,
    continuous2: !!state.bounds2?.continuo2,
  };
}

// The report request of the current calculation (state null: nothing computed yet).
export function buildReportPayload(
  state: CalcState | null,
  ctx: ReportContext,
  opts: ReportPayloadOptions,
): ScrewConnectorReportData {
  const { meta } = ctx;
  return {
    language: opts.language,
    date: localDate(reportDate(meta)),
    generatedAt: localDateTime(new Date()),
    calculationName: meta.calculationName,
    projectName: meta.projectName,
    author: meta.author ?? "",
    full: opts.version === "full",
    connectionType: ctx.connectionType,
    family: state?.screwRec?.family ?? ctx.family,
    rows: ctx.rows,
    cols: ctx.cols,
    serviceClass: ctx.serviceClass,
    element1: element(1, state, ctx),
    element2: element(2, state, ctx),
    blockReason: opts.blockReason || state?.blocked || "",
    images: {
      hero: base64Of(opts.views.hero),
      iso: base64Of(opts.views.iso),
      front: base64Of(opts.views.front),
      side: base64Of(opts.views.side),
      screw: opts.screwImage ?? "",
    },
    references: {
      screwId: state?.screwRec?.id ?? null,
      element1MaterialId: opts.references.element1TimberMaterialId ?? null,
      element2MaterialId: opts.references.element2TimberMaterialId ?? null,
      loadDurationClassCode: opts.references.loadDurationClassCode ?? null,
    },
    calculation: state ? calculation(state) : null,
  };
}
